/**
 * `OnboardingFlow` — orchestrator for the SHA v0 patient onboarding
 * flow per [`sha_v0_patient_onboarding_flow_design.md`](../../../../sha_v0_patient_onboarding_flow_design.md)
 * §3 composition.
 *
 * Lifts the subscriber + getState pattern from `auth/auth-flow.ts`,
 * `recovery/recovery-flow.ts`, and `activation/activation-flow.ts`.
 * State transitions via discriminated-union reducer (`applyEvent`
 * from `./onboarding-state.ts`); orchestrator subscribes to existing
 * activation + auth flows as observers and dispatches transition
 * events on their state changes.
 *
 * **Composition (memo §3):**
 *   §3.1 ActivationFlow — observed; on `activation.kind === 'success'`,
 *        dispatches `activation-completed`
 *   §3.2 Recovery setup ceremony — election surface only at Sprint 7
 *        close (Sprint 8+ ceremony substantive composes with DEV3
 *        wire-contract `IProjectionServiceClient.initiateRecoverySetup`
 *        etc.). v0 scaffold accepts `submitRecoveryTier()` + immediate
 *        synthetic `recovery-setup-completed` (replaced with substantive
 *        ceremony at Sprint 8+)
 *   §3.3 AuthenticationFlow — observed; on `auth.kind === 'session-active'`,
 *        dispatches `sign-in-session-active`
 *   §3.4 Account v0 home — landing fires once `account-home-landing`
 *        terminal state reached; app shell renders `<AccountHomeView />`
 *
 * **Doctrinal posture (memo §6 binding):**
 *   - NO SHT BACKDOOR — orchestrator does NOT touch envelope-crypto
 *     adapter; cleartext key material does not flow through this module
 *   - Recovery UX honesty — orchestrator preserves "content unrecoverable"
 *     framing as recovery-setup-failure consequence; UI renders per
 *     `<RecoverySetupStatus />` doctrine-anchored copy
 *   - Patient-fiduciary non-delegable authority — orchestrator drives
 *     election but does NOT delegate fiduciary scope; patient elects
 *     tier + provisions elements
 */
import { applyEvent, isTerminal, } from './onboarding-state.js';
export class OnboardingFlow {
    state = { kind: 'activation-pending' };
    subscribers = new Set();
    unsubActivation;
    unsubAuth;
    constructor(options) {
        // §3.1 ActivationFlow observer per memo §3.1
        this.unsubActivation = options.activationFlow.subscribe((activationState) => {
            if (this.state.kind === 'activation-pending' &&
                activationState.kind === 'complete') {
                this.dispatch({ kind: 'activation-completed' });
            }
        });
        // §3.3 AuthenticationFlow observer per memo §3.3
        this.unsubAuth = options.authenticationFlow.subscribe((authState) => {
            if (this.state.kind === 'initial-sign-in' &&
                authState.kind === 'session-active') {
                this.dispatch({ kind: 'sign-in-session-active' });
            }
        });
    }
    getState() {
        return this.state;
    }
    subscribe(callback) {
        this.subscribers.add(callback);
        callback(this.state);
        return () => this.subscribers.delete(callback);
    }
    dispatch(event) {
        try {
            this.state = applyEvent(this.state, event);
        }
        catch (e) {
            this.state = {
                kind: 'error',
                code: 'invalid-transition',
                message: e instanceof Error ? e.message : String(e),
            };
        }
        for (const sub of this.subscribers) {
            sub(this.state);
        }
    }
    submitRecoveryTier(tier) {
        this.dispatch({ kind: 'submit-recovery-tier', tier });
    }
    notifyRecoverySetupComplete() {
        this.dispatch({ kind: 'recovery-setup-completed' });
    }
    notifyBootstrapComplete() {
        this.dispatch({ kind: 'bootstrap-completed' });
    }
    cancel(reason) {
        if (isTerminal(this.state))
            return;
        this.dispatch({ kind: 'cancel', reason });
    }
    restart() {
        this.state = { kind: 'activation-pending' };
        for (const sub of this.subscribers) {
            sub(this.state);
        }
    }
    /**
     * Tear down upstream-flow subscriptions. Call on app shell unmount
     * or when the orchestrator is replaced; prevents memory leaks via
     * stale subscribers.
     */
    dispose() {
        this.unsubActivation();
        this.unsubAuth();
    }
}
//# sourceMappingURL=onboarding-flow.js.map